import Icon from '@klaviyo-mkt/gatsby-components/atoms/icon';
import { generateClassName } from '@klaviyo-mkt/gatsby-components/shared/utils/functions';

import { useGetCurriculumList } from '../../hooks/useGetCurriculumList';

import type { ICurriculumList, IEnrichLesson, ILesson, TEnrichedLesson } from '../../../graphql/types';

import * as styles from './styles/curriculumList.module.scss';

const enrichLessons = (
  { lessons, curriculumList }: IEnrichLesson): TEnrichedLesson[] =>
  lessons.map((lesson) => {
    const curriculumLesson = curriculumList[lesson.thirdPartyId] || {};

    return {
      ...curriculumLesson,
      ...lesson,
    };
  });

interface ICurriculumListProps {
  coursePath: string;
  curriculumList: ICurriculumList;
  lessons: ILesson[];
  showHeadline?: boolean;
}

export const CurriculumList = ({
  coursePath,
  curriculumList,
  lessons,
  showHeadline = true,
}: ICurriculumListProps) => {
  const curriculumListValues = Object.values(curriculumList);
  const enrichedLessons: TEnrichedLesson[] = curriculumListValues.length > 0 ? enrichLessons({
    lessons, curriculumList,
  }) : lessons;
  const quiz = enrichedLessons.find(lesson => lesson.icon === 'QUIZ');
  const lessonsBeforeQuiz = quiz ? enrichedLessons.slice(0, enrichedLessons.indexOf(quiz)) : enrichedLessons;
  const nextSteps = quiz ? enrichedLessons.slice(enrichedLessons.indexOf(quiz)) : enrichedLessons;

  return (
    <div className={styles.curriculumList}>
      {showHeadline && <h2 className={styles.headline}>Course outline</h2>}
      <section>
        <h3 className={styles.sectionHeading}>Lessons</h3>
        <ul className={styles.list}>
          {lessonsBeforeQuiz.map((lesson, index) => (
            <li className={styles.listItem} key={index}>
              <a className={styles.link} href={`${coursePath}/${lesson.pathId}`}>
                <div className={generateClassName([styles.iconContainer, lesson.isCompleted && styles.completed])}>
                  {lesson.isCompleted && <Icon fillColor="currentColor" iconName="Check" size={1.6} />}
                </div>
                <span>{lesson.title}</span>
              </a>
            </li>
          ))}
        </ul>
      </section>
      {quiz && nextSteps.length > 0 && (
        <section>
          <h3 className={styles.sectionHeading}>Next steps</h3>
          <ul className={styles.list}>
            {nextSteps.map((lesson, index) => (
              <li className={styles.listItem} key={index}>
                <a className={styles.link} href={`${coursePath}/${lesson.pathId}`}>
                  <div className={generateClassName([styles.iconContainer, lesson.isCompleted && styles.completed])}>
                    {lesson.isCompleted && <Icon fillColor="currentColor" iconName="Check" size={1.6} />}
                  </div>
                  <span>{lesson.title}</span>
                </a>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
};

type TCurriculumListWithDataProps = Omit<ICurriculumListProps, 'curriculumList'> & { courseId: string; };

export const CurriculumListWithData = ({
  courseId,
  ...props
}: TCurriculumListWithDataProps) => {
  const curriculumList = useGetCurriculumList(courseId);

  return <CurriculumList {...props} curriculumList={curriculumList} />;
};
